<template>
  <form class="w-full" @submit.prevent>
    <div :class="['mb-5', { 'mb-2': !helperText }]">
      <!-- Label (only show when validation is 'none') -->
      <label v-if="title" :for="id" :class="labelClasses">
        {{ title }}
      </label>

      <!-- Input -->
      <div class="relative">
        <!-- Icon (SVG string passed in) -->
        <div
          v-if="icon"
          class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none"
          v-html="icon"
        ></div>

        <input
          :id="id"
          v-model="inputValue"
          :type="type"
          :aria-describedby="helperText ? `${id}-helper-text` : undefined"
          :class="inputClasses"
          :placeholder="placeholder"
          :disabled="disabled"
        />

        <!-- Clear Button (X Icon) -->
        <button
          v-if="inputValue"
          type="button"
          class="absolute inset-y-0 right-0 flex items-center pr-3.5"
          :class="clearButtonClasses"
          @click="clearInput"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="w-5 h-5"
            :class="{
              'w-3 h-3': size === 'small',
              'w-5 h-5': size === 'base',
              'w-6 h-6': size === 'large'
            }"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <!-- Helper Text -->
      <p v-if="helperText" :id="`${id}-helper-text`" :class="helperTextClasses">
        <span v-if="validation === 'success'" class="font-medium"
          >Alright!</span
        >
        <span v-if="validation === 'error'" class="font-medium">Oops!</span>
        {{ helperText }}
      </p>
    </div>
  </form>
</template>

<script lang="ts" setup>
import { computed } from "vue";

const emit = defineEmits(["update:modelValue"]);
const props = withDefaults(
  defineProps<{
    modelValue: string;
    id?: string;
    title?: string;
    type?: string;
    placeholder?: string;
    helperText?: string | null;
    icon?: string | null;
    validation?: "success" | "error" | "none";
    disabled?: boolean;
    size?: "small" | "base" | "large";
  }>(),
  {
    id: "",
    title: "",
    type: "text",
    placeholder: "",
    helperText: null,
    icon: null,
    validation: "none",
    disabled: false,
    size: "base"
  }
);

// Input model binding
const clearInput = () => {
  inputValue.value = ""; // Clear the input field
};

const inputValue = computed({
  get() {
    return props.modelValue;
  },
  set(val) {
    emit("update:modelValue", val);
  }
});

// Computed class for label
// Computed class for label
const labelClasses = computed(() => {
  const sizeClass =
    props.size === "small"
      ? "text-xs"
      : props.size === "large"
      ? "text-base"
      : "text-sm";

  if (props.validation === "success") {
    return `block mb-2 font-medium ${sizeClass} text-green-700 dark:text-green-500`;
  } else if (props.validation === "error") {
    return `block mb-2 font-medium ${sizeClass} text-red-700 dark:text-red-500`;
  } else if (props.disabled) {
    return `block mb-2 font-medium ${sizeClass} text-gray-400 dark:text-gray-500`;
  } else {
    return `block mb-2 font-medium ${sizeClass} text-gray-900 dark:text-white`;
  }
});

// Computed class for input
const inputClasses = computed(() => {
  const baseClasses = [
    "bg-gray-50 rounded-lg border block w-full dark:bg-gray-700 dark:text-white dark:border-gray-600",
    props.size === "large"
      ? "p-4 text-base"
      : props.size === "small"
      ? "p-2 text-xs"
      : "p-2.5 text-sm" // Default to base size
  ];

  if (props.validation === "success") {
    baseClasses.push(
      "bg-green-50 border-green-500 text-green-900 placeholder-green-700 focus:ring-green-500 focus:border-green-500 dark:text-green-400 dark:border-green-500 dark:placeholder-green-500"
    );
  } else if (props.validation === "error") {
    baseClasses.push(
      "bg-red-50 border-red-500 text-red-900 placeholder-red-700 focus:ring-red-500 focus:border-red-500 dark:bg-gray-700 dark:text-red-500 dark:placeholder-red-500 dark:border-red-500"
    );
  } else if (props.disabled) {
    baseClasses.push(
      "bg-gray-200 text-gray-400 border-gray-300 placeholder-gray-500 cursor-not-allowed dark:bg-gray-600 dark:text-gray-500 dark:border-gray-500 dark:placeholder-gray-600"
    );
  } else {
    baseClasses.push(
      "bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500 dark:placeholder-gray-400 dark:border-gray-600 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
    );
  }

  if (props.icon) {
    baseClasses.push("pl-10");
  } else {
    baseClasses.push("pl-3");
  }

  return baseClasses.join(" ");
});

const helperTextClasses = computed(() => {
  const sizeClass =
    props.size === "small"
      ? "text-xs"
      : props.size === "large"
      ? "text-base"
      : "text-sm";

  const baseClasses = ["mt-2", sizeClass];

  if (props.validation === "success") {
    baseClasses.push("text-green-600 dark:text-green-500");
  } else if (props.validation === "error") {
    baseClasses.push("text-red-600 dark:text-red-500");
  } else if (props.disabled) {
    baseClasses.push("text-gray-500 dark:text-gray-400");
  } else {
    baseClasses.push("text-gray-500 dark:text-gray-400");
  }

  return baseClasses.join(" ");
});

// Computed class for clear button (X icon)
const clearButtonClasses = computed(() => {
  let buttonClass =
    "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300";

  if (props.validation === "success") {
    buttonClass =
      "text-green-500 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300";
  } else if (props.validation === "error") {
    buttonClass =
      "text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300";
  }

  return buttonClass;
});
</script>

<style scoped></style>
